import { useApiClient, usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { moduleAreaForPath } from '@hetivo/hetivo-lib-types';
import { useQuery } from '@tanstack/react-query';

import { useTenantModules } from '&/hooks/use-tenant-modules';

import { createServiceReminderApi, serviceReminderKeys } from '../api';
import { SERVICE_REMINDERS_PATH, SERVICE_REMINDERS_VIEW_PERMISSION } from '../paths';
import { useMasterSelectionFilters } from './use-master-selection-filters';

/**
 * The Overdue count of the Reminders sidebar badge (decision #24), under the master selection (decision #27).
 *
 * The request is made only for a tenant that owns the module the reminders path belongs to and a user who
 * holds `service-plans:list`; without either the server would refuse it, so none is sent. It shares its
 * query key with the page's unfiltered stats call (one request when both are mounted) and falls under the
 * `stats` prefix that snooze and dismiss invalidate. A failure is no badge: `undefined`, never a retry loop.
 */
export function useServiceReminderOverdueCount(): number | undefined {
  const client = useApiClient('admin-auth');
  const api = createServiceReminderApi(client);
  const selection = useMasterSelectionFilters();
  const { hasModule } = useTenantModules();
  const { hasPermission } = usePermissions();

  const area = moduleAreaForPath(SERVICE_REMINDERS_PATH);
  const enabled = area !== undefined && hasModule(area) && hasPermission(SERVICE_REMINDERS_VIEW_PERMISSION);

  const { data } = useQuery({
    enabled,
    gcTime: 5 * 60_000,
    queryFn: () => api.getServiceReminderStats(selection),
    queryKey: serviceReminderKeys.stats(selection),
    retry: false,
    select: (stats) => stats.overdue,
    staleTime: 60_000,
  });

  return data;
}
